import { BrowserRouter, Navigate, Route, Routes } from 'react-router'
import { RequireAuth, RequireRole } from './auth/require-auth.js'
import { SessionProvider } from './auth/session-context.js'
import { AppShell } from './layout/app-shell.js'
import { ErrorBoundary } from './layout/error-boundary.js'
import { ThemeProvider } from './layout/theme.js'
import { AgentPage } from './pages/agent-page.js'
import { AgentsPage } from './pages/agents-page.js'
import { AuditPage } from './pages/audit-page.js'
import { ConversationPage } from './pages/conversation-page.js'
import { ConversationsPage } from './pages/conversations-page.js'
import { ChannelsPage } from './pages/channels-page.js'
import { CredentialsPage } from './pages/credentials-page.js'
import { JobFailuresPage } from './pages/job-failures-page.js'
import { LoginPage } from './pages/login-page.js'
import { MembersPage } from './pages/members-page.js'
import { NotFoundPage } from './pages/not-found-page.js'
import { OverviewPage } from './pages/overview-page.js'
import { PromptPage } from './pages/prompt-page.js'
import { FunnelPage } from './pages/funnel-page.js'
import { ProductsPage } from './pages/products-page.js'
import { PromptsPage } from './pages/prompts-page.js'
import { WorkspaceLayout, WorkspaceRedirect } from './workspace/workspace-context.js'

/**
 * Дерево маршрутов.
 *
 * Воркспейс входит в адрес (`/w/:workspaceId/...`), потому что это делает
 * ссылку на экран самодостаточной: её можно отправить коллеге, сохранить
 * в закладки и открыть в двух вкладках с разными воркспейсами одновременно.
 *
 * Проверки роли стоят и здесь, и на сервере. Здесь — чтобы не показывать
 * человеку экран, который всё равно ответит отказом; на сервере — потому
 * что только там проверка настоящая. Убрать серверную, посчитав, что
 * маршрут и так закрыт, значит остаться без защиты вообще: адрес в браузере
 * меняется свободно.
 *
 * Разбивки бандла нет намеренно: экранов пока десяток, и на таком размере
 * ленивая загрузка добавляет сетевых запросов больше, чем экономит байтов.
 * Её место — этап 7, когда появятся тяжёлые экраны аналитики.
 */
export function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <SessionProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route element={<RequireAuth />}>
                <Route
                  index
                  element={
                    <WorkspaceRedirect>
                      <NoWorkspaces />
                    </WorkspaceRedirect>
                  }
                />

                <Route path="/w/:workspaceId" element={<WorkspaceLayout />}>
                  <Route element={<AppShell />}>
                    <Route index element={<OverviewPage />} />

                    {/*
                      Список участников виден и наблюдателю: знать, кто ещё
                      работает в воркспейсе, полезно всем. Изменение прав
                      внутри экрана закрыто ролью admin — и здесь, и на сервере.
                    */}
                    <Route path="members" element={<MembersPage />} />

                    <Route path="conversations" element={<ConversationsPage />} />
                    <Route path="conversations/:conversationId" element={<ConversationPage />} />

                    {/*
                      Промпты и агенты видны наблюдателю: знать, по какой
                      инструкции работает бот, полезно всем, кто разбирает
                      диалоги. Правка внутри экрана закрыта ролью admin —
                      и здесь, и на сервере.
                    */}
                    <Route path="prompts" element={<PromptsPage />} />
                    <Route path="prompts/:promptId" element={<PromptPage />} />
                    <Route path="agents" element={<AgentsPage />} />
                    {/*
                      Каталог виден всем: оператор должен знать, что именно
                      продаёт агент в его диалоге. Правка внутри экрана
                      закрыта ролью admin — и здесь, и на сервере.
                    */}
                    <Route path="products" element={<ProductsPage />} />
                    <Route path="funnel" element={<FunnelPage />} />
                    <Route path="agents/:agentId" element={<AgentPage />} />

                    <Route
                      path="channels"
                      element={
                        <RequireRole role="admin">
                          <ChannelsPage />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="credentials"
                      element={
                        <RequireRole role="admin">
                          <CredentialsPage />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="audit"
                      element={
                        <RequireRole role="admin">
                          <AuditPage />
                        </RequireRole>
                      }
                    />
                    <Route
                      path="jobs"
                      element={
                        <RequireRole role="admin">
                          <JobFailuresPage />
                        </RequireRole>
                      }
                    />

                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </SessionProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

function NoWorkspaces() {
  return (
    <div className="mx-auto max-w-lg px-6 py-16 text-center">
      <h1 className="text-lg font-semibold text-ink">Нет доступных воркспейсов</h1>
      <p className="mt-1 text-sm text-muted">
        Попросите владельца добавить вас в участники. Первый воркспейс создаётся на сервере
        командой <code className="font-mono">pnpm --filter @ai-sales/api bootstrap</code>.
      </p>
    </div>
  )
}
